import React, { useEffect, useMemo, useRef, useState } from 'react';

import * as S from './styles';
import { Loading } from '../loading';
import Badge from '../badge';
import ButtonV2 from '../button/button_v2';
import { theme } from '../../styles/theme';
import iconPenEdit from '../../assets/icons/icon-pen-edit.png';
import iconTrashRed from '../../assets/icons/icon-trash-red.png';
import iconClose from '../../assets/icons/icons-close-gray.svg';
// Mesma seta do select_v2, para o campo ficar igual aos outros selects da tela.
import iconArrowDown from '../../assets/icons/icons8-expand-arrow-32.png';

/**
 * Select de valor unico com busca e criacao ao digitar, no padrao do Notion.
 *
 * O componente e generico de proposito: ele nao conhece endpoint nenhum. Quem
 * usa passa `options` e os callbacks de criar/editar/remover, e trata a API.
 *
 * O valor e uma STRING (o nome), nao um id - e assim que o equipamento e
 * gravado na receita (`equipamento_aplicacao`, VarChar 255). Isso mantem a
 * compatibilidade com receitas antigas, em que o campo era texto livre: se o
 * `value` nao existir mais na lista, ele continua aparecendo como badge.
 *
 * Props:
 *  - value: string selecionada ('' quando vazio)
 *  - onChange(nome): chamado ao selecionar, criar ou limpar
 *  - options: [{ id, nome }]
 *  - loading / erro: estados da lista
 *  - onCreate(nome) -> Promise<{ nome }>  (opcional; sem ela nao oferece criar)
 *  - onEdit(id, nome) -> Promise<{ nome }> (opcional)
 *  - onDelete(id) -> Promise<void>        (opcional)
 *  - label, placeholder, disabled, maxLength, marginBottom, minHeight, width,
 *    marginTop, legenda, messageErro, labelWithAsterisk
 */
// Indice de "nada realcado": nenhuma opcao aparece ativa ate o usuario passar o
// mouse ou navegar pelo teclado.
const SEM_REALCE = -1;

const InputBadge = ({ ...props }) => {
  const {
    value = '',
    onChange,
    options = [],
    loading = false,
    erro = false,
    onCreate,
    onEdit,
    onDelete,
    label = '',
    placeholder = '',
    disabled = false,
    maxLength = 255,
    legenda,
    messageErro,
    labelWithAsterisk = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [highlight, setHighlight] = useState(SEM_REALCE);
  const [editandoId, setEditandoId] = useState(null);
  const [editandoNome, setEditandoNome] = useState('');
  const [acaoEmCurso, setAcaoEmCurso] = useState(false);
  // Alem do booleano, guarda QUAL equipamento esta em acao, para o spinner
  // aparecer na linha certa quando ha varios na lista.
  const [acaoIdEmCurso, setAcaoIdEmCurso] = useState(null);
  const [erroAcao, setErroAcao] = useState('');

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  // Guarda sincrona: o clique no botao Criar tambem borbulha para a linha, e o
  // acaoEmCurso do state so vale no render seguinte - sem isso, um clique
  // criaria o equipamento duas vezes.
  const criandoRef = useRef(false);

  const normalizar = (texto) =>
    String(texto ?? '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const buscaLimpa = busca.replace(/\s+/g, ' ').trim();

  const filtradas = useMemo(() => {
    if (!buscaLimpa) return options;
    const alvo = normalizar(buscaLimpa);
    return options.filter((o) => normalizar(o.nome).includes(alvo));
  }, [options, buscaLimpa]);

  // So oferece criar quando o texto digitado ainda nao existe na conta. A
  // comparacao e normalizada para nao sugerir criar "PULVERIZACAO" quando ja
  // existe "Pulverização" - e essa divergencia que a tarefa quer eliminar.
  const podeCriar =
    typeof onCreate === 'function' &&
    !!buscaLimpa &&
    !options.some((o) => normalizar(o.nome) === normalizar(buscaLimpa));

  const totalNavegavel = filtradas.length + (podeCriar ? 1 : 0);

  useEffect(() => {
    setHighlight(SEM_REALCE);
  }, [buscaLimpa, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentMouseDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        fechar();
      }
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [isOpen]);

  const fechar = () => {
    setIsOpen(false);
    setBusca('');
    setEditandoId(null);
    setEditandoNome('');
    setErroAcao('');
  };

  const abrir = () => {
    if (disabled) return;
    setIsOpen(true);
    // O foco precisa esperar o input existir no DOM apos o render.
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const estaSelecionado = (nome) =>
    !!value && normalizar(nome) === normalizar(value);

  // O campo guarda UM equipamento (o que vai para equipamento_aplicacao).
  // Clicar na opcao ja selecionada mantem ela selecionada (so fecha o menu),
  // em vez de limpar o campo - pra limpar, o usuario usa o "x" da badge.
  const alternar = (opcao) => {
    if (estaSelecionado(opcao.nome)) {
      fechar();
      return;
    }
    selecionar(opcao.nome);
  };

  const selecionar = (nome) => {
    if (typeof onChange === 'function') onChange(nome);
    fechar();
  };

  // Chamado pelo onClose do Badge, que ja fez o stopPropagation - nao depende
  // de receber o evento.
  const limpar = () => {
    if (disabled) return;
    if (typeof onChange === 'function') onChange('');
  };

  const criar = async () => {
    if (!podeCriar || criandoRef.current) return;
    criandoRef.current = true;
    setAcaoEmCurso(true);
    setErroAcao('');
    try {
      const criado = await onCreate(buscaLimpa);
      // Usa o nome devolvido pela API: ela normaliza espacos e pode reaproveitar
      // um registro existente com grafia diferente da digitada.
      selecionar((criado && criado.nome) || buscaLimpa);
    } catch (e) {
      setErroAcao(mensagemDeErro(e, 'Não foi possível salvar o equipamento.'));
    } finally {
      criandoRef.current = false;
      setAcaoEmCurso(false);
    }
  };

  const iniciarEdicao = (event, opcao) => {
    event.stopPropagation();
    setEditandoId(opcao.id);
    setEditandoNome(opcao.nome);
    setErroAcao('');
  };

  const cancelarEdicao = () => {
    setEditandoId(null);
    setEditandoNome('');
    setErroAcao('');
  };

  const confirmarEdicao = async (event) => {
    if (event) event.stopPropagation();
    const nome = editandoNome.replace(/\s+/g, ' ').trim();
    if (!nome || acaoEmCurso) return;

    const original = options.find((o) => o.id === editandoId);
    setAcaoEmCurso(true);
    setAcaoIdEmCurso(editandoId);
    setErroAcao('');
    try {
      const atualizado = await onEdit(editandoId, nome);
      const novoNome = (atualizado && atualizado.nome) || nome;
      // Se o item renomeado era o selecionado, acompanha o novo nome para o
      // campo nao ficar apontando para um texto que nao existe mais na lista.
      if (original && value && normalizar(original.nome) === normalizar(value)) {
        if (typeof onChange === 'function') onChange(novoNome);
      }
      cancelarEdicao();
    } catch (e) {
      setErroAcao(mensagemDeErro(e, 'Não foi possível renomear o equipamento.'));
    } finally {
      setAcaoIdEmCurso(null);
      setAcaoEmCurso(false);
    }
  };

  const remover = async (event, opcao) => {
    event.stopPropagation();
    if (acaoEmCurso) return;
    setAcaoEmCurso(true);
    setAcaoIdEmCurso(opcao.id);
    setErroAcao('');
    const eraOSelecionado = estaSelecionado(opcao.nome);

    try {
      await onDelete(opcao.id);
      // Removida a opcao que estava escolhida, o campo fica vazio: manter a
      // badge de um equipamento que nao existe mais na lista da conta daria a
      // entender que ele ainda pode ser reutilizado.
      if (eraOSelecionado && typeof onChange === 'function') onChange('');
    } catch (e) {
      setErroAcao(mensagemDeErro(e, 'Não foi possível remover o equipamento.'));
    } finally {
      setAcaoIdEmCurso(null);
      setAcaoEmCurso(false);
    }
  };

  const mensagemDeErro = (e, fallback) => {
    const apiMsg = e?.response?.data?.errors || e?.response?.data?.message;
    return typeof apiMsg === 'string' && apiMsg ? apiMsg : fallback;
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      fechar();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (totalNavegavel === 0) return;
      const paraBaixo = event.key === 'ArrowDown';
      setHighlight((atual) => {
        // Saindo de "nada realcado", desce para a primeira e sobe para a ultima.
        if (atual < 0) return paraBaixo ? 0 : totalNavegavel - 1;
        return (atual + (paraBaixo ? 1 : -1) + totalNavegavel) % totalNavegavel;
      });
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      // Sem realce: Enter cria o que foi digitado, ou, se o nome ja existe,
      // escolhe a primeira opcao da lista filtrada.
      if (highlight < 0) {
        if (podeCriar) criar();
        else if (filtradas[0]) alternar(filtradas[0]);
      } else if (highlight < filtradas.length) {
        const opcao = filtradas[highlight];
        if (opcao) alternar(opcao);
      } else if (podeCriar) {
        criar();
      }
      return;
    }

    // Backspace com a busca vazia limpa a badge, como no Notion.
    if (event.key === 'Backspace' && !busca && value) {
      if (typeof onChange === 'function') onChange('');
    }
  };

  const temValor = value !== '' && value !== null && value !== undefined;
  const isFloating = temValor || isOpen;

  const renderMenu = () => {
    if (loading) return <S.StateRow>Carregando equipamentos...</S.StateRow>;
    if (erro) {
      return (
        <S.StateRow erro>Não foi possível carregar os equipamentos salvos.</S.StateRow>
      );
    }

    const vazio = filtradas.length === 0 && !podeCriar;
    if (vazio) {
      return (
        <S.StateRow>
          {options.length === 0
            ? 'Nenhum equipamento salvo ainda. Digite para criar o primeiro.'
            : 'Nenhum equipamento encontrado.'}
        </S.StateRow>
      );
    }

    return (
      <>
        <S.MenuHint>
          {podeCriar ? 'Selecione uma opção ou crie uma' : 'Selecione uma opção'}
        </S.MenuHint>

        {filtradas.map((opcao, index) =>
          editandoId === opcao.id ? (
            <S.EditRow key={opcao.id} onClick={(e) => e.stopPropagation()}>
              <input
                autoFocus
                value={editandoNome}
                maxLength={maxLength}
                onChange={(e) => setEditandoNome(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') confirmarEdicao(e);
                  if (e.key === 'Escape') cancelarEdicao();
                }}
              />
              {acaoIdEmCurso === opcao.id ? (
                <S.AcoesLoading>
                  <Loading size="18px" borderWidth="2px" noMargin />
                </S.AcoesLoading>
              ) : (
                <>
                  <ButtonV2
                    width="auto"
                    height="24px"
                    value="Salvar"
                    color={theme.colors.green.Verde_AgroReceita}
                    onClick={confirmarEdicao}
                  />
                  <S.IconButton
                    type="button"
                    title="Cancelar"
                    onClick={cancelarEdicao}
                  >
                    <img src={iconClose} alt="Cancelar" />
                  </S.IconButton>
                </>
              )}
            </S.EditRow>
          ) : (
            <S.Option
              key={opcao.id}
              isHighlighted={editandoId === null && highlight === index}
              onClick={() => alternar(opcao)}
            >
              <S.BadgeSlot grow>
                <Badge
                  colorBg={theme.colors.gray.Cinza_2}
                  colorText={theme.colors.white.Branco}
                >
                  {opcao.nome}
                </Badge>
              </S.BadgeSlot>

              {acaoIdEmCurso === opcao.id ? (
                <S.AcoesLoading>
                  <Loading size="18px" borderWidth="2px" noMargin />
                </S.AcoesLoading>
              ) : (
                <span className="acoes">
                  {typeof onEdit === 'function' && (
                    <S.IconButton
                      type="button"
                      title="Renomear"
                      disabled={acaoEmCurso}
                      onClick={(e) => iniciarEdicao(e, opcao)}
                    >
                      <img src={iconPenEdit} alt="Renomear" />
                    </S.IconButton>
                  )}
                  {typeof onDelete === 'function' && (
                    <S.IconButton
                      danger
                      type="button"
                      title="Remover da lista"
                      disabled={acaoEmCurso}
                      onClick={(e) => remover(e, opcao)}
                    >
                      <img src={iconTrashRed} alt="Remover da lista" />
                    </S.IconButton>
                  )}
                </span>
              )}
            </S.Option>
          ),
        )}

        {podeCriar && (
          <S.CreateRow
            isHighlighted={highlight === filtradas.length}
            onClick={criar}
          >
            <ButtonV2
              width="auto"
              height="30px"
              value={acaoEmCurso ? 'Salvando...' : 'Criar'}
              color={theme.colors.green.Verde_AgroReceita}
              loading={acaoEmCurso}
              onClick={criar}
            />
            <S.BadgeSlot>
              <Badge
                colorBg={theme.colors.gray.Cinza_2}
                colorText={theme.colors.white.Branco}
              >
                {buscaLimpa}
              </Badge>
            </S.BadgeSlot>
          </S.CreateRow>
        )}
      </>
    );
  };

  return (
    <S.Container
      ref={containerRef}
      width={props.width}
      minHeight={props.minHeight}
      marginBottom={props.marginBottom}
      marginTop={props.marginTop}
    >
      {!!label && (
        <S.Label
          isFloating={isFloating}
          erro={erro}
          disabled={disabled}
          onClick={abrir}
        >
          {labelWithAsterisk && <b>*</b>}
          {label}
        </S.Label>
      )}

      <S.Control erro={erro} disabled={disabled} isOpen={isOpen} onClick={abrir}>
        {temValor && (
          <S.BadgeSlot>
            <Badge
              colorBg={theme.colors.gray.Cinza_2}
              colorText={theme.colors.white.Branco}
              IsClose={!disabled}
              onClose={limpar}
              closeTitle="Limpar equipamento"
            >
              {value}
            </Badge>
          </S.BadgeSlot>
        )}

        {/* Fechado e vazio, quem preenche o interior do campo e o proprio
            label (mesmo comportamento do InputV2) - por isso nao existe texto
            de placeholder aqui, senao os dois se sobrepoem. */}
        {isOpen && (
          <S.SearchInput
            ref={inputRef}
            value={busca}
            disabled={disabled}
            maxLength={maxLength}
            placeholder={temValor ? '' : placeholder}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={onKeyDown}
          />
        )}

        <S.Caret isOpen={isOpen}>
          <img src={iconArrowDown} alt="" />
        </S.Caret>
      </S.Control>

      {isOpen && <S.Menu>{renderMenu()}</S.Menu>}

      {(erroAcao || (erro && messageErro) || legenda) && (
        <S.Legenda erro={!!erroAcao || erro}>
          {erroAcao || (erro && messageErro) || legenda}
        </S.Legenda>
      )}
    </S.Container>
  );
};

export default InputBadge;
